// ./src/utils/videoUtils.ts

export interface VideoValidationResult {
  isValid: boolean;
  error?: string;
}

export interface VideoInfo {
  duration: number;
  width: number;
  height: number;
  size: number;
  type: string;
}

export interface VideoMetadata {
  duration?: number;
  width?: number;
  height?: number;
  size: number;
  type: string;
  name: string;
}

// Supported video formats
const SUPPORTED_VIDEO_TYPES = [
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/quicktime", // .mov files
] as const;

const SUPPORTED_VIDEO_EXTENSIONS = [".mp4", ".webm", ".ogg", ".mov"] as const;

// Configuration
export const DEFAULT_MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB
export const DEFAULT_MAX_VIDEO_DURATION = 600; // 10 minutes in seconds

export interface VideoValidationConfig {
  maxSize?: number;
  maxDuration?: number;
  allowedTypes?: readonly string[];
}

/**
 * Get a comma-separated string of supported video MIME types
 */
export const getSupportedVideoTypesString = (): string => {
  return SUPPORTED_VIDEO_TYPES.join(",");
};

/**
 * Get a human-readable description of supported video formats
 */
export const getSupportedVideoFormatsDescription = (): string => {
  return SUPPORTED_VIDEO_EXTENSIONS.map((ext) =>
    ext.toUpperCase().replace(".", "")
  ).join(", ");
};

/**
 * Validate if a file is a supported video type
 */
export const validateVideoFile = (
  file: File,
  config: VideoValidationConfig = {}
): VideoValidationResult => {
  const {
    maxSize = DEFAULT_MAX_VIDEO_SIZE,
    allowedTypes = SUPPORTED_VIDEO_TYPES,
  } = config;

  // Check if file exists
  if (!file) {
    return {
      isValid: false,
      error: "No file provided",
    };
  }

  // Check file type
  const isTypeSupported = allowedTypes.includes(file.type as any);
  const hasValidExtension = SUPPORTED_VIDEO_EXTENSIONS.some((ext) =>
    file.name.toLowerCase().endsWith(ext)
  );

  if (!isTypeSupported && !hasValidExtension) {
    return {
      isValid: false,
      error: `Unsupported video format. Please use ${getSupportedVideoFormatsDescription()}`,
    };
  }

  // Check file size
  if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    const fileSizeMB = Math.round(file.size / (1024 * 1024));
    return {
      isValid: false,
      error: `Video file is too large (${fileSizeMB}MB). Maximum size is ${maxSizeMB}MB`,
    };
  }

  return { isValid: true };
};

/**
 * Get video metadata including duration and dimensions
 */
export const getVideoMetadata = (file: File): Promise<VideoMetadata> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);

      const metadata: VideoMetadata = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        size: file.size,
        type: file.type,
        name: file.name,
      };

      resolve(metadata);
    };

    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error("Failed to load video metadata"));
    };

    video.src = URL.createObjectURL(file);
  });
};

/**
 * Validate video duration
 */
export const validateVideoDuration = (
  duration: number,
  maxDuration: number = DEFAULT_MAX_VIDEO_DURATION
): VideoValidationResult => {
  if (duration > maxDuration) {
    const maxMinutes = Math.floor(maxDuration / 60);
    const maxSeconds = maxDuration % 60;
    const durationMinutes = Math.floor(duration / 60);
    const durationSeconds = Math.round(duration % 60);

    return {
      isValid: false,
      error: `Video is too long (${durationMinutes}:${durationSeconds
        .toString()
        .padStart(2, "0")}). Maximum duration is ${maxMinutes}:${maxSeconds
        .toString()
        .padStart(2, "0")}`,
    };
  }

  return { isValid: true };
};

/**
 * Format duration in seconds to a human-readable string (MM:SS)
 */
export const formatVideoDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

/**
 * Format file size to human-readable string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

/**
 * Create upload notes for video metadata
 */
export const createVideoUploadNotes = (metadata: VideoMetadata): string => {
  const notes: string[] = [];

  if (metadata.duration) {
    notes.push(`Duration: ${formatVideoDuration(metadata.duration)}`);
  }

  if (metadata.width && metadata.height) {
    notes.push(`Dimensions: ${metadata.width}x${metadata.height}`);
  }

  notes.push(`Size: ${formatFileSize(metadata.size)}`);
  notes.push(`Format: ${metadata.type}`);

  return notes.join(" | ");
};

/**
 * Generate video thumbnail from file
 */
export const generateVideoThumbnail = (
  file: File,
  timeInSeconds: number = 1
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      reject(new Error("Failed to get canvas context"));
      return;
    }

    video.onloadedmetadata = () => {
      // Seek to specific time
      video.currentTime = Math.min(timeInSeconds, video.duration);
    };

    video.onseeked = () => {
      // Set canvas dimensions to video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to data URL
      const thumbnailUrl = canvas.toDataURL("image/jpeg", 0.8);

      // Clean up
      window.URL.revokeObjectURL(video.src);

      resolve(thumbnailUrl);
    };

    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error("Failed to generate video thumbnail"));
    };

    video.src = URL.createObjectURL(file);
  });
};
